/*
================================================================================
|                                                                                |
|  FILE: src/sections/ecorevivepage/ImpactMetrics.js                               |
|  PURPOSE: Impact Metrics section with visual data and project outcomes.        |
|                                                                                |
================================================================================
*/
import React from 'react';

export default function ImpactMetrics() {
    const metrics = [
        {
            number: "100%",
            label: "Water Purification",
            description: "ChistyLOS technology implementation",
            icon: "💧",
            color: "from-blue-400 to-blue-600"
        },
        {
            number: "6",
            label: "SDGs Supported",
            description: "Direct alignment with UN goals",
            icon: "🎯",
            color: "from-green-400 to-green-600"
        },
        {
            number: "∞",
            label: "Scalable Model",
            description: "Closed-loop eco-restoration",
            icon: "♾️",
            color: "from-purple-400 to-purple-600"
        },
        {
            number: "2050",
            label: "Future Vision",
            description: "Commitment to future generations",
            icon: "🔮",
            color: "from-orange-400 to-orange-600"
        }
    ];

    return (
        <section className="py-20 bg-gradient-to-r from-green-50 to-blue-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">
                        Measurable Impact & Outcomes
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Project EcoRevive delivers more than clean water and fertile land—it drives rural employment, 
                        enhances food security, and lays the foundation for climate-smart communities.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
                    {metrics.map((metric, index) => (
                        <div key={index} className="text-center group">
                            <div className={`w-20 h-20 bg-gradient-to-r ${metric.color} rounded-full flex items-center justify-center mx-auto mb-4 text-3xl group-hover:scale-110 transition-transform duration-300`}>
                                {metric.icon}
                            </div>
                            {/* UPDATED LINE BELOW */}
                            <div className="text-[38px] sm:text-4xl font-bold text-gray-900 mb-2">{metric.number}</div>
                            <div className="text-lg font-semibold text-gray-700 mb-2">{metric.label}</div>
                            <div className="text-sm text-gray-500">{metric.description}</div>
                        </div>
                    ))}
                </div>

                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-3xl p-8 lg:p-12">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-6">
                                A Scalable, Closed-Loop Model
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-start gap-4">
                                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Rural Employment</h4>
                                        <p className="text-gray-600">Creating sustainable job opportunities in local communities</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Food Security</h4>
                                        <p className="text-gray-600">Enhancing agricultural productivity through land restoration</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Climate-Smart Communities</h4>
                                        <p className="text-gray-600">Building resilient infrastructure for future generations</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <img 
                                src="/images/voltalake.jpg" 
                                alt="Environmental restoration impact" 
                                className="w-full h-80 object-cover rounded-2xl shadow-lg"
                            />
                            <div className="absolute -top-4 -left-4 bg-white p-4 rounded-xl shadow-lg">
                                <p className="text-sm font-semibold text-gray-900">Volta Lake</p>
                                <p className="text-xs text-gray-500">An Ideal water body</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}